import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, Calendar } from 'lucide-react';

export const ExperienceSection = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [scrollProgress, setScrollProgress] = useState(0);

  const experiences = t('experience.items', { returnObjects: true });

  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById('experience');
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const sectionHeight = rect.height;
      const scrolled = -rect.top;
      const progress = Math.min(Math.max(scrolled / (sectionHeight * 0.8), 0), 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      id="experience"
      data-testid="experience-timeline"
      ref={ref}
      className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 bg-[#0A0E27]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#F8FAFC] mb-4">
            {t('experience.title')}
          </h2>
          <p className="text-[#CBD5E1] text-lg md:text-xl">
            {t('experience.subtitle')}
          </p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto" data-testid="timeline-container">
          {/* Timeline Line */}
          <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#22D3EE]/30">
            <motion.div
              className="w-full bg-[#22D3EE]"
              style={{
                height: `${scrollProgress * 100}%`,
                transition: 'height 0.3s ease-out',
              }}
            />
          </div>

          {/* Timeline Items */}
          <div className="space-y-8 md:space-y-12">
            {experiences.map((exp, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline Dot */}
                <div
                  className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 w-6 h-6 rounded-full bg-[#22D3EE] border-4 border-[#0A0E27] z-10"
                  data-testid="timeline-dot"
                />

                {/* Content Card */}
                <div
                  className={`ml-12 md:ml-0 ${
                    index % 2 === 0 ? 'md:pr-12 md:w-1/2' : 'md:pl-12 md:w-1/2 md:ml-auto'
                  }`}
                >
                  <div
                    data-testid="experience-card"
                    className="relative bg-[#1A1F3A] rounded-xl p-6 border-l-4 border-[#22D3EE] hover:bg-[#1F2542] hover:shadow-[0_0_25px_rgba(34,211,238,0.15)] transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-xl font-semibold text-[#22D3EE]">
                        {exp.position}
                      </h3>
                    </div>

                    <h4 className="text-lg font-medium text-[#F8FAFC] mb-2">
                      {exp.company}
                    </h4>

                    <div className="flex flex-wrap gap-3 mb-4 text-sm text-[#94A3B8]">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {exp.period}
                      </div>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        {exp.location}
                      </div>
                    </div>

                    <p className="text-[#CBD5E1] text-sm leading-relaxed mb-4">
                      {exp.description}
                    </p>

                    {exp.achievements && exp.achievements.length > 0 && (
                      <ul className="space-y-2">
                        {exp.achievements.map((achievement, achIndex) => (
                          <li
                            key={achIndex}
                            className="text-sm text-[#CBD5E1] flex items-start"
                          >
                            <span className="text-[#22D3EE] mr-2">•</span>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
